import { html, render } from 'lit-html'
import { ifDefined } from 'lit-html/directives/if-defined'
import { styleMap } from 'lit-html/directives/style-map'
import { register } from '@polleverywhere/hmr-custom-element'
import { bemBlock } from '@polleverywhere/bem-helpers'
import Controls from './controls'
import { cutlassEvent, contextualize } from '@polleverywhere/cutlass'
import './style.scss'

function template (controls, limiter = 0, expanded = false) {
  const templates = controls.map((control, index) => {
    switch (control.name) {
      case 'search-field':
        return searchTemplate(control)

      default: {
        const tagDuplicate = index < limiter - 1
        return buttonTemplate(control, tagDuplicate)
      }
    }
  })

  if (limiter > 0 && limiter < controls.length) {
    // Render `More` button in open or closed states.
    const moreControl = Object.assign({}, Controls.find(c => c.name === 'more'))
    if (expanded) {
      moreControl.label = 'Close menu'
    }

    // Copy all controls to the dropdown container before splicing the list.
    const allControls = templates.concat([])

    // Now, we can remove all controls past the limit index.
    templates.splice(limiter - 1, templates.length)

    // Wrap all controls into a drawer container underneath a `More` button.
    const styles = {
      '--count': allControls.length,
      '--limit': limiter
    }
    templates.push(html`
      <div name='drawer' style=${styleMap(styles)}>
        ${buttonTemplate(moreControl)}
        <div>${allControls}</div>
      </div>
    `)
  }

  return templates
}

/**
 * Constructs a lit template for the search control.
 * @param {Object} control Config object
 * @return {Object}
 */
function searchTemplate (control) {
  return html`
    <div class='element-search-box ${bem('search')}' role='search'>
      <input type='text' name='${control.name}'
        ?disabled=${control.disabled}
        title='${control.label}' aria-label='${control.label}'
        placeholder='${control.label}' @input=${handleEvent} />
      <div class='element-search-box__search'></div>
      <button class='element-search-box__clear' name='${control.name}:clear'
        title='${control.label} clear' aria-label='${control.label} clear'
        ?disabled=${control.disabled}
        @click=${handleSearchClear}></button>
    </div>`
}

function handleSearchClear (e) {
  e.target.parentElement.querySelector('input').value = ''
  handleEvent.call(this, e)
}

/**
 * Constructs a lit template for the button control.
 * @param {Object} control Config object
 * @param {boolean} tagDuplicate Mark button as having a duplicate in the `More`
 *    drawer and the visible container. We use this attribute to show/hide
 *    controls based on media queries.
 * @return {Object}
 */
function buttonTemplate (control, tagDuplicate = false) {
  return html`
    <button class=${bem('button')}
      name='${control.name}'
      title='${control.label}' aria-label='${control.label}'
      ?primary=${control.primary}
      ?secondary=${!control.primary}
      ?disabled=${control.disabled}
      ?duplicate=${tagDuplicate}
      data-glyph-name='${ifDefined(control.icon)}'
      @click=${handleEvent} />`
}

function handleExpand (e) {
  if (this.hasAttribute('expand')) {
    this.removeAttribute('expand')
  } else {
    this.setAttribute('expand', '')
  }

  // Re-render template to ensure expander control label is updated.
  renderTemplate(this)
}

/**
 * Handles all control events and emits all events in the format =>
 * `[event_type]:[control_name]` with Event object passed as an argument.
 * @param {Event} e
 */
function handleEvent (e) {
  if (e.target.hasAttribute('disabled')) return

  const eventType = e.type
  const controlName = e.target.name
  const event = new CustomEvent(`${eventType}:${controlName}`, {
    bubbles: true,
    detail: { event: e }
  })

  // Do additional processing when clicking on `more` control.
  if (controlName === 'more') {
    handleExpand.call(this, e)
  }

  if (controlName !== 'search-field') {
    cutlassEvent(this, `${controlName}:${eventType}`)
  }

  this.dispatchEvent(event)
}

/**
 * Returns a list of control objects for each valid name.
 * @param {string[]} names List of unique control names.
 * @return {Object[]} Control objects
 */
function getControls (names) {
  // Create convenience mapping of control objects by name.
  const ControlMap = Object.fromEntries(
    Controls.map((control) => [control.name, control]))
  return names
    .filter((name) => name in ControlMap)
    .map((name) => ControlMap[name])
}

/**
 * Renders the controls listed in `primary` and `secondary` attributes.
 * @param {Element} el ControlSet element to render controls into.
 */
function renderTemplate (el) {
  if (!el.isConnected) return

  const primaryControlList = el.getAttribute('primary') || ''
  const secondaryControlList = el.getAttribute('secondary') || ''
  const limiter = parseInt(el.getAttribute('limit')) || undefined
  const expanded = el.hasAttribute('expand')

  const primaryControls = getControls(primaryControlList.split(','))
    .map((control) => Object.assign({}, control, { primary: true }))
  const secondaryControls = getControls(secondaryControlList.split(','))

  const allDisabled = el.hasAttribute('disabled')
  const disabledControls = el.getAttribute('disabled-controls')
  const disabledList = disabledControls ? disabledControls.split(',') : []

  const controls = primaryControls.concat(secondaryControls)
    .map((control) => Object.assign({}, control, {
      disabled: allDisabled || disabledList.includes(control.name)
    }))

  render(template(controls, limiter, expanded), el, {
    eventContext: el
  })
}

/**
 * Renders a group or set of controls. There are two types of controls, primary
 * vs secondary, which renders as primary vs secondary CTAs to the user. Each
 * control has a unique `name` by which they can be listed as comma-separated
 * values in order of most to least important. Controls are then rendered in
 * reverse-order from right to left, with the most important control rendered to
 * the far-right and the least important to the far-left.
 *
 * @element control-set
 *
 * @fires click:<control-name> - Fired by all button controls on click.
 * @fires input:<control-name> - Fired by input controls on input.
 * @fires click:<control-name>:<action> - Fired by additional actions performed
 *    within a control that exposes multiple actions, e.g. `search-field`'s
 *    clear button.
 *
 * @attr {string} primary - Comma-separated list of control names to render as
 *    primary controls, ordered from most to least important.
 * @attr {string} secondary - Comma-separated list of control names to render as
 *    secondary controls, ordered from most to least important.
 * @attr {number} limit - Specify max number of buttons allowed to render
 *    inline. If number of controls exceed the limit, a new expander control is
 *    rendered in the last inline slot which opens/closes the drawer containing
 *    the remaining buttons. If limit value is the same as the count, no drawer
 *    or extra control is rendered.
 * @attr {boolean} expand - Expand or open overflow drawer when `limit` is set.
 *    Consumers can additionally inspect existence of this attribute to
 *    determine if the overflow drawer is in an expanded vs collapsed state.
 * @attr {boolean} labelless - Condenses controls to show icons only and alters
 *    slightly the behavior of the expandable dropdown.
 * @attr {boolean} labelless-ghost - Same as labelless but renders ghost buttons
 *    with padding around the icon.
 * @attr {boolean} disabled - When attribute exists, disables _all_ controls.
 * @attr {string} disabled-controls - Comma-separated list of control names to individually disable.
 * @attr {string} direction - If "column", renders buttons in a vertical layout.
 */
export default class ControlSet extends HTMLElement {
  static get tagName () { return 'control-set' }
  static get observedAttributes () {
    return [
      'primary',
      'secondary',
      'limit',
      'expand',
      'labelless',
      'labelless-ghost',
      'disabled',
      'disabled-controls',
      'direction'
    ]
  }

  constructor () {
    super()

    contextualize(this, this.tagName)
  }

  connectedCallback () {
    renderTemplate(this)
  }

  attributeChangedCallback () {
    renderTemplate(this)
  }
}

const bem = bemBlock(ControlSet.tagName)
register(module, ControlSet.tagName, ControlSet)
